import { useCallback, useMemo, useState } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

// Slider bounds and default per the Ambassadors earnings calculator spec.
const MIN_SIGNATURE_ESTIMATE = 1000;
const MAX_SIGNATURE_ESTIMATE = 200000;
const SIGNATURE_ESTIMATE_STEP = 500;
const DEFAULT_SIGNATURE_ESTIMATE = 10000;

// Midpoint of the $0.30-$0.50 per-signature payout range.
const PER_SIGNATURE_ESTIMATE_RATE = 0.4;

// With the 500-signature step, the boundary falls between 12,500 signatures ($5,000, still a range)
// and 13,000 ($5,200, computed).
const EARNINGS_RANGE_THRESHOLD = 5000;
const EARNINGS_RANGE_MIN = 400;
const EARNINGS_RANGE_MAX = 5000;

type Result = ModelHookResult<
	{
		signatureEstimate: number;
		minSignatureEstimate: number;
		maxSignatureEstimate: number;
		signatureEstimateStep: number;
		// Raw numbers only - localization must happen client-side (see component),
		// never in this hook, otherwise the SSR i18n guard throws
		// (docs/front-end/troubleshooting.md - "Unexpected use of i18n.xyz server-side").
		earningsEstimate: number;
		// when true, render earningsRangeMin-earningsRangeMax instead of earningsEstimate
		isEarningsEstimateRange: boolean;
		earningsRangeMin: number;
		earningsRangeMax: number;
	},
	{
		onSliderChange: (value: number) => void;
		onSliderChangeEnd: (value: number) => void;
	}
>;

export function useEarningsCalculator(): Result {
	const track = useTracking();

	const [signatureEstimate, setSignatureEstimate] = useState(DEFAULT_SIGNATURE_ESTIMATE);

	const earningsEstimate = useMemo(
		() => Math.round(signatureEstimate * PER_SIGNATURE_ESTIMATE_RATE),
		[signatureEstimate],
	);

	const onSliderChange = useCallback((value: number) => {
		setSignatureEstimate(value);
	}, []);

	const onSliderChangeEnd = useCallback(
		(value: number) => {
			setSignatureEstimate(value);
			void track('ambassadors_calculator_interact', { signature_estimate: value });
		},
		[track],
	);

	return {
		data: {
			signatureEstimate,
			minSignatureEstimate: MIN_SIGNATURE_ESTIMATE,
			maxSignatureEstimate: MAX_SIGNATURE_ESTIMATE,
			signatureEstimateStep: SIGNATURE_ESTIMATE_STEP,
			earningsEstimate,
			isEarningsEstimateRange: earningsEstimate <= EARNINGS_RANGE_THRESHOLD,
			earningsRangeMin: EARNINGS_RANGE_MIN,
			earningsRangeMax: EARNINGS_RANGE_MAX,
		},
		actions: {
			onSliderChange,
			onSliderChangeEnd,
		},
	};
}
